/**
 * Polling/reconnect hook for streaming messages (thread-switch / refresh recovery)
 *
 * When the UI loads a thread that contains an assistant message with
 * status "streaming" (e.g. the user navigates to a thread where the
 * backend is still processing, or the page was refreshed mid-stream),
 * this hook attempts recovery in three stages:
 *
 *  1. Backend state fetch — fetches the current accumulated message
 *     state from Redis via the /state endpoint. This provides consistent
 *     state without complex client-side event replay or cache merging.
 *
 *  2. SSE reconnect — uses the lastEventId from the backend state to
 *     subscribe for incremental updates via the stream endpoint. Stages 1
 *     and 2 repeat, with backoff, until /state says the message ended.
 *
 *  3. Polling fallback — once Redis no longer holds the message
 *     (not_found), the hook polls the messages REST endpoint every 3 s
 *     until the message transitions to a terminal state.
 *
 * The three recovery strategies live in ./streamingRecovery.ts.
 * This hook is responsible only for orchestration and lifecycle management.
 *
 * DESIGN NOTES:
 *
 * • The effect depends on `streamingMessageId` (a stable string | null)
 *   instead of the full `messages` array.  This prevents the effect
 *   from re-running — and aborting a live reconnect — every time a
 *   stream event updates message content.  The effect only fires when
 *   the streaming message ID appears or disappears.
 *
 * • We deliberately do NOT call setThreadStreaming inside this hook.
 *   The DB-loaded message already has status:'streaming', which causes
 *   AssistantUIAdapter.hasStreamingMessage to be true and keeps the
 *   send button in cancel state.  Adding a separate setThreadStreaming
 *   call would flip isLoading, re-trigger this effect's cleanup, and
 *   abort the in-progress reconnect.
 *
 * • This hook fetches state from the backend Redis store, which is the
 *   single source of truth for streaming state.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ThreadMessage } from '@/types/messages';
import type { LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';
import { logger } from '@/utils/logger';
import { POLL_INTERVAL_MS } from '@/constants/ui.constants';
import {
  resumeUntilTerminal,
  startRestPolling,
  type SetMessages,
} from './streamingRecovery';

interface UseStreamingPollerProps {
  projectId: string | null;
  threadId: string | null;
  messages: ThreadMessage[];
  isLoading: boolean;
  setMessages: SetMessages;
  accessToken: string;
  streamProcessor: LLMEventStreamProcessor;
}

export function useStreamingPoller({
  projectId,
  threadId,
  messages,
  isLoading,
  setMessages,
  accessToken,
  streamProcessor,
}: UseStreamingPollerProps): void {
  const stopPollingRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Tracks which message ID is currently being recovered to prevent
  // re-triggering while a reconnect or poll is already in progress.
  const recoveryMessageIdRef = useRef<string | null>(null);

  // Keep a ref to accessToken so startReconnect can always read the
  // latest value without being included in its useCallback deps.
  // If accessToken were a dep, every OIDC token refresh would recreate
  // startReconnect → re-trigger useEffect → stopAll() aborts the
  // in-flight reconnect mid-stream and restarts it, erasing content.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  // Derive a stable primitive from messages.  The effect depends on
  // this value, not the full array, so SSE content updates don't
  // cancel the reconnect mid-flight.
  const streamingMessageId = useMemo<string | null>(() => {
    if (!threadId) return null;
    const msg = messages.find(
      (m) =>
        m.threadId === threadId &&
        m.role === 'assistant' &&
        m.status === 'streaming',
    );
    return msg?.id ?? null;
  }, [messages, threadId]);

  const stopPolling = useCallback(() => {
    stopPollingRef.current?.();
    stopPollingRef.current = null;
  }, []);

  // The aborted reconnect clears its own id override; clearing here would
  // also drop a live turn's.
  const abortReconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const stopAll = useCallback(() => {
    stopPolling();
    abortReconnect();
    recoveryMessageIdRef.current = null;
  }, [stopPolling, abortReconnect]);

  /** Stage 3 entry-point: starts the REST polling interval. */
  const startPolling = useCallback(
    (pollProjectId: string, pollThreadId: string) => {
      stopPolling();
      stopPollingRef.current = startRestPolling(
        pollProjectId,
        pollThreadId,
        POLL_INTERVAL_MS,
        setMessages,
        () => {
          recoveryMessageIdRef.current = null;
        },
      );
    },
    [stopPolling, setMessages],
  );

  /**
   * Orchestrates the three recovery stages for a streaming message that
   * needs to be reconnected (thread loaded mid-stream or after refresh).
   *
   * Stages 1 ⇄ 2 until the message ends → Stage 3 when Redis lost it.
   */
  const startReconnect = useCallback(
    async (
      reconnectProjectId: string,
      reconnectThreadId: string,
      messageId: string,
    ) => {
      logger.info(
        `[useStreamingPoller] startReconnect: ` +
          `thread=${reconnectThreadId} msg=${messageId}`,
      );

      abortReconnect();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const outcome = await resumeUntilTerminal(
        reconnectProjectId,
        reconnectThreadId,
        messageId,
        () => accessTokenRef.current,
        controller,
        streamProcessor,
        setMessages,
      );
      if (outcome === 'aborted') {
        logger.debug('[useStreamingPoller] SSE reconnect aborted');
        return;
      }

      abortControllerRef.current = null;
      recoveryMessageIdRef.current = null;
      if (outcome === 'not_found') {
        startPolling(reconnectProjectId, reconnectThreadId);
      }
    },
    [abortReconnect, setMessages, streamProcessor, startPolling],
  );

  useEffect(() => {
    if (!threadId || !projectId) {
      stopAll();
      return;
    }

    // If the user-initiated stream is actively running, the live SSE
    // connection in useMessageSender drives all updates — don't interfere.
    if (isLoading) {
      stopAll();
      return;
    }

    if (!streamingMessageId) {
      stopAll();
      return;
    }

    // Guard: already recovering this message (reconnect in flight).
    if (recoveryMessageIdRef.current === streamingMessageId) {
      return;
    }

    recoveryMessageIdRef.current = streamingMessageId;
    startReconnect(projectId, threadId, streamingMessageId);

    return stopAll;
  }, [
    threadId,
    streamingMessageId,
    isLoading,
    projectId,
    startReconnect,
    stopAll,
  ]);
}
